/**
 * PairSearchDropdown Component
 *
 * Main dropdown component integrating search, keyboard navigation, and results display.
 * Uses @floating-ui/react for positioning and Framer Motion for animations.
 * Features a liquid glass effect with backdrop blur.
 * Integrates with watchlist store for favorites.
 */

import {
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import * as Sentry from '@sentry/react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import type { ECexName } from 'web3';

import { type ExchangeId, EXCHANGES_METADATA } from '@/shared/cex-sdk';
import { ROUTE_PATHS } from '@/shared/consts/routes';
import { SmartFloatingPortal as FloatingPortal } from '@/shared/ui/SmartFloatingPortal';
import { toEnglishLayout } from '@/shared/utils';
import { isMac } from '@/shared/utils/platform';

import { useCexSymbolStatuses } from '@/entities/cex';
import { type ProjectItems, useWatchlistStore } from '@/entities/watchlist';

import { useTypedSelector } from 'store';

import { GroupedResultsList } from './GroupedResultsList';
import { QuickFilters } from './QuickFilters';
import { SearchInput } from './SearchInput';

import { useKeyboardNavigation } from '../lib/useKeyboardNavigation';
import { useSearchEntities } from '../lib/useSearchEntities';
import type {
  ArbitrageSearchResult,
  BalancerSearchResult,
  DisplayMode,
  EntityTypeFilter,
  PairSearchResult,
  SearchResult,
} from '../types';

const TYPE_FILTERS_STORAGE_KEY = 'mm-dashboard:pair-search:type-filters';
const RECOGNIZED_PLATFORM_FILTERS = [
  // CEX platforms - must match src/web3/cexs.ts
  'binance',
  'bybit',
  'mexc',
  'kucoin',
  'okx',
  'gate',
  'whitebit',
  'htx',
  'kraken',
  'bitget',
  'bitmart',
  'lbank',
  'bingx',
  'coinstore',
  'hotcoin',
  'indodax',
  'xt',
  // DEX platforms - ALL variants from src/web3/dexs.ts IDexName (excluding Solana)
  'uniswap_v2',
  'uniswap_v2:base',
  'uniswap_v2:robinhood',
  'uniswap_v3',
  'uniswap_v3:polygon',
  'uniswap_v3:arbitrum_one',
  'uniswap_v3:base',
  'uniswap_v3:robinhood',
  'pancakeswap_v2',
  'pancakeswap_v2:robinhood',
  'pancakeswap_v3:bsc',
  'pancakeswap_v3:robinhood',
  'pancakeswap_inf_cl:bsc',
  'quickswap_v2',
  'sushiswap_v2',
  'sushiswap_v3:haqq',
  'camelot_v2',
  'swopfi_v2:unit0',
  'astradex_v2:airdao',
  'aerodrome_v3:base',
  'aerodrome_v3_2:base',
  'aerodrome_v2_volatile:base',
  // Solana DEXes
  'jupiter:solana',
  'raydium',
];

function loadTypeFilters(): EntityTypeFilter[] {
  try {
    const stored = localStorage.getItem(TYPE_FILTERS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        // Only persist entity-type filters; platform filters are stored separately.
        const validFilters: EntityTypeFilter[] = ['arbitrage', 'balancer'];
        return parsed.filter((f): f is EntityTypeFilter => validFilters.includes(f));
      }
    }
  } catch {
    // Ignore parse errors
  }
  return [];
}

function saveTypeFilters(filters: EntityTypeFilter[]): void {
  try {
    localStorage.setItem(TYPE_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Ignore storage errors
  }
}

function extractPlatformFilters(query: string): { cleanQuery: string; platforms: string[] } {
  const parts = query
    .split(',')
    .map(p => p.trim())
    .filter(Boolean);
  if (parts.length <= 1) {
    return { cleanQuery: query.trim(), platforms: [] };
  }

  const platforms: string[] = [];
  const leftover: string[] = [];

  parts.forEach(part => {
    const lower = part.toLowerCase();
    if (RECOGNIZED_PLATFORM_FILTERS.includes(lower)) {
      platforms.push(lower);
    } else {
      leftover.push(part);
    }
  });

  return {
    cleanQuery: leftover.join(' ').trim(),
    platforms,
  };
}

const SUPPORTED_CEX_EXCHANGES = new Set<ExchangeId>(EXCHANGES_METADATA.map(({ id }) => id));
const COPY_FEEDBACK_TIMEOUT_MS = 1500;

interface PairSearchDropdownProps {
  /** Render prop for trigger button */
  trigger: (props: { onClick: () => void; isOpen: boolean }) => ReactNode;

  /** Optional callback when dropdown closes */
  onClose?: () => void;
}

interface PairSearchDropdownErrorBoundaryProps {
  children: ReactNode;
  isAdmin: boolean;
  onClose: () => void;
}

const PairSearchDropdownErrorBoundary = ({
  children,
  isAdmin,
  onClose,
}: PairSearchDropdownErrorBoundaryProps) => {
  const [errorDetails, setErrorDetails] = useState('');
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const handleCopy = useCallback(async () => {
    if (!errorDetails || !navigator?.clipboard?.writeText) {
      setCopyState('failed');
      return;
    }

    try {
      await navigator.clipboard.writeText(errorDetails);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    } finally {
      window.setTimeout(() => setCopyState('idle'), COPY_FEEDBACK_TIMEOUT_MS);
    }
  }, [errorDetails]);

  const fallback = (
    <div className="px-4 py-6 text-sm text-white">
      <div className="text-base font-semibold text-white">Something went wrong</div>
      <p className="mt-1 text-xs text-white/70">
        We could not load the search dropdown. Please close and try again.
      </p>

      {isAdmin && (
        <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-3">
          <div className="flex items-center justify-between gap-3 text-xs text-white/80">
            <span className="font-medium">Details</span>
            <button
              type="button"
              onClick={handleCopy}
              className="rounded-md border border-white/10 px-2 py-1 text-[11px] text-white/80 transition hover:text-white"
            >
              {copyState === 'copied'
                ? 'Copied'
                : copyState === 'failed'
                  ? 'Copy failed'
                  : 'Copy details'}
            </button>
          </div>
          <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words text-[11px] leading-snug text-white/60">
            {errorDetails || 'No details available.'}
          </pre>
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-white/10 px-3 py-1 text-xs text-white/80 transition hover:text-white"
        >
          Close
        </button>
      </div>
    </div>
  );

  return (
    <Sentry.ErrorBoundary
      onError={(error, componentStack) => {
        const parts = [error?.message, error?.stack];
        if (typeof componentStack === 'string' && componentStack.trim()) {
          parts.push(componentStack.trim());
        }
        setErrorDetails(parts.filter(Boolean).join('\n\n'));
      }}
      onReset={() => {
        setErrorDetails('');
        setCopyState('idle');
      }}
      fallback={fallback}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

interface PairSearchDropdownPanelProps {
  inputValue: string;
  searchQuery: string;
  displayMode: DisplayMode;
  typeFilters: EntityTypeFilter[];
  platformFilters: string[];
  isOpen: boolean;
  searchInputRef: RefObject<HTMLInputElement>;
  onInputValueChange: (value: string) => void;
  onSearchQueryChange: (value: string) => void;
  onDisplayModeChange: (value: DisplayMode) => void;
  onTypeFiltersChange: (filters: EntityTypeFilter[]) => void;
  onPlatformFiltersChange: (filters: string[]) => void;
  onSelectResult: (result: SearchResult) => void;
  onClose: () => void;
  onToggleFavorite: (
    type: 'pair' | 'arbitrage' | 'balancer' | 'project',
    projectId: number,
    itemId: number,
  ) => void;
  onAddProjectToWatchlist: (items: ProjectItems) => void;
  onRemoveProjectFromWatchlist: (items: ProjectItems) => void;
  favoriteProjectsSet: Set<string>;
}

const PairSearchDropdownPanel = ({
  inputValue,
  searchQuery,
  displayMode,
  typeFilters,
  platformFilters,
  isOpen,
  searchInputRef,
  onInputValueChange,
  onSearchQueryChange,
  onDisplayModeChange,
  onTypeFiltersChange,
  onPlatformFiltersChange,
  onSelectResult,
  onClose,
  onToggleFavorite,
  onAddProjectToWatchlist,
  onRemoveProjectFromWatchlist,
  favoriteProjectsSet,
}: PairSearchDropdownPanelProps) => {
  const {
    allResults,
    isLoading,
    favoritePairsSet,
    favoriteArbitragesSet,
    favoriteBalancersSet,
    projectTotalPairs,
    refreshProjectBotsData,
  } = useSearchEntities({
    searchQuery,
    displayMode,
    typeFilters,
    platformFilters,
    enabled: isOpen,
  });

  const handleProjectExpand = useCallback(
    (projectId: number) => {
      void refreshProjectBotsData(projectId);
    },
    [refreshProjectBotsData],
  );

  const { focusedIndex, handleKeyDown, activedescendant } = useKeyboardNavigation({
    results: allResults,
    onSelect: onSelectResult,
    onClose,
    isOpen,
  });

  const cexStatusRequests = useMemo(() => {
    return allResults
      .filter((r): r is PairSearchResult => r.type === 'pair' && r.pairType === 'cex')
      .map(pair => ({
        cex: pair.platform as ECexName,
        base: pair.baseToken,
        quote: pair.quoteToken,
      }));
  }, [allResults]);

  const { data: symbolStatuses } = useCexSymbolStatuses({
    items: cexStatusRequests,
    enabled: isOpen && cexStatusRequests.length > 0,
  });

  return (
    <>
      {/* Search Input */}
      <div className="px-4 pt-4 pb-3">
        <SearchInput
          value={inputValue}
          onChange={value => {
            onInputValueChange(value);
            const { cleanQuery, platforms } = extractPlatformFilters(value);
            onSearchQueryChange(cleanQuery);
            onPlatformFiltersChange(platforms);
          }}
          onKeyDown={handleKeyDown}
          isOpen={isOpen}
          activedescendant={activedescendant}
          autoFocus
          inputRef={searchInputRef}
        />
      </div>

      {/* Quick Filters */}
      <div className="px-4 pb-3">
        <QuickFilters
          displayMode={displayMode}
          onDisplayModeChange={onDisplayModeChange}
          typeFilters={typeFilters}
          onTypeFiltersChange={onTypeFiltersChange}
          platformFilters={platformFilters}
          onPlatformFiltersChange={onPlatformFiltersChange}
        />
      </div>

      {/* Divider */}
      <div className="h-px bg-white/10" />

      {/* Results List */}
      <div className="flex-1 overflow-hidden">
        <GroupedResultsList
          results={allResults}
          focusedIndex={focusedIndex}
          focusedResult={
            focusedIndex >= 0 && focusedIndex < allResults.length
              ? allResults[focusedIndex]
              : undefined
          }
          onSelectResult={onSelectResult}
          onToggleFavorite={onToggleFavorite}
          onAddProjectToWatchlist={onAddProjectToWatchlist}
          onRemoveProjectFromWatchlist={onRemoveProjectFromWatchlist}
          favoritePairs={favoritePairsSet}
          favoriteArbitrages={favoriteArbitragesSet}
          favoriteBalancers={favoriteBalancersSet}
          favoriteProjects={favoriteProjectsSet}
          isLoading={isLoading}
          searchQuery={searchQuery}
          symbolStatuses={symbolStatuses}
          projectTotalPairs={projectTotalPairs}
          onProjectExpand={handleProjectExpand}
        />
      </div>
    </>
  );
};

export function PairSearchDropdown({ trigger, onClose }: PairSearchDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('active');
  const [typeFilters, setTypeFilters] = useState<EntityTypeFilter[]>(loadTypeFilters);
  const [platformFilters, setPlatformFilters] = useState<string[]>([]);
  const [useFixedPosition, setUseFixedPosition] = useState(false);
  const navigate = useNavigate();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isAdmin = useTypedSelector(store => store.auth.isAdmin);

  // Watchlist store for determining default filter
  const favoritePairs = useWatchlistStore(s => s.favoritePairs);
  const favoriteArbitrages = useWatchlistStore(s => s.favoriteArbitrages);
  const favoriteBalancers = useWatchlistStore(s => s.favoriteBalancers);
  const favoriteProjects = useWatchlistStore(s => s.favoriteProjects);

  // Watchlist store actions
  const toggleFavoritePair = useWatchlistStore(s => s.toggleFavoritePair);
  const toggleFavoriteArbitrage = useWatchlistStore(s => s.toggleFavoriteArbitrage);
  const toggleFavoriteBalancer = useWatchlistStore(s => s.toggleFavoriteBalancer);
  const toggleFavoriteProject = useWatchlistStore(s => s.toggleFavoriteProject);
  const addProjectItems = useWatchlistStore(s => s.addProjectItems);
  const removeProjectItems = useWatchlistStore(s => s.removeProjectItems);

  // Save typeFilters to localStorage when they change
  useEffect(() => {
    saveTypeFilters(typeFilters);
  }, [typeFilters]);

  // Set default display mode when dropdown opens (only on open, not on watchlist changes)
  // Note: typeFilters are NOT reset - they persist across sessions
  useEffect(() => {
    if (isOpen) {
      const hasItems =
        favoritePairs.length > 0 ||
        favoriteArbitrages.length > 0 ||
        favoriteBalancers.length > 0 ||
        favoriteProjects.length > 0;
      setDisplayMode(hasItems ? 'watchlist' : 'active');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run on isOpen change
  }, [isOpen]);

  // Floating UI setup
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(8), // 8px gap from trigger
      flip(), // Flip to opposite side if no space
      shift({ padding: 8 }), // Shift along axis to stay in viewport
    ],
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
  });

  // Dismiss on click outside
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  const favoriteProjectsSet = useMemo(() => new Set(favoriteProjects), [favoriteProjects]);

  // Handle favorite toggle (now requires projectId for composite key)
  const handleToggleFavorite = useCallback(
    (type: 'pair' | 'arbitrage' | 'balancer' | 'project', projectId: number, itemId: number) => {
      switch (type) {
        case 'pair':
          toggleFavoritePair(projectId, itemId);
          break;
        case 'arbitrage':
          toggleFavoriteArbitrage(projectId, itemId);
          break;
        case 'balancer':
          toggleFavoriteBalancer(projectId, itemId);
          break;
        case 'project':
          toggleFavoriteProject(projectId);
          break;
      }
    },
    [toggleFavoritePair, toggleFavoriteArbitrage, toggleFavoriteBalancer, toggleFavoriteProject],
  );

  // Handle adding entire project to watchlist (using composite keys)
  const handleAddProjectToWatchlist = useCallback(
    (items: ProjectItems) => {
      addProjectItems(items);
    },
    [addProjectItems],
  );

  // Handle removing entire project from watchlist (using composite keys)
  const handleRemoveProjectFromWatchlist = useCallback(
    (items: ProjectItems) => {
      removeProjectItems(items);
    },
    [removeProjectItems],
  );

  // Route generation for pairs
  const getPairRoute = useCallback((result: PairSearchResult): string | null => {
    const params = {
      projectId: String(result.projectId),
      pairId: String(result.pairId),
    };

    if (result.pairType === 'cex') {
      const exchangeId = result.platform as ExchangeId;
      const hasSdkSupport = SUPPORTED_CEX_EXCHANGES.has(exchangeId);

      if (hasSdkSupport) {
        return generatePath(ROUTE_PATHS.pair, {
          projectId: params.projectId,
          venue: result.pairType,
          pairId: params.pairId,
        });
      }

      return generatePath(ROUTE_PATHS.cexPair, params);
    }

    if (result.pairType === 'dex') {
      return generatePath(ROUTE_PATHS.dexPair, params);
    }

    if (result.pairType === 'sol') {
      return generatePath(ROUTE_PATHS.solPair, params);
    }

    console.warn('Unhandled pair type for navigation', result);
    return null;
  }, []);

  // Route generation for arbitrages
  const getArbitrageRoute = useCallback((result: ArbitrageSearchResult): string => {
    return generatePath(ROUTE_PATHS.arbitrage, {
      projectId: String(result.projectId),
      arbitrageId: String(result.arbitrageId),
    });
  }, []);

  // Route generation for balancers
  const getBalancerRoute = useCallback((result: BalancerSearchResult): string => {
    return generatePath(ROUTE_PATHS.balancer, {
      projectId: String(result.projectId),
      balancerId: String(result.balancerId),
    });
  }, []);

  // Unified route generation for all result types
  const getResultRoute = useCallback(
    (result: SearchResult): string | null => {
      switch (result.type) {
        case 'pair':
          return getPairRoute(result);
        case 'arbitrage':
          return getArbitrageRoute(result);
        case 'balancer':
          return getBalancerRoute(result);
        case 'project':
          return generatePath(ROUTE_PATHS.project, {
            id: String(result.projectId),
          });
        default:
          console.warn('Unhandled result type for navigation', result);
          return null;
      }
    },
    [getPairRoute, getArbitrageRoute, getBalancerRoute],
  );

  // Handle result selection
  const handleSelectResult = useCallback(
    (result: SearchResult) => {
      const route = getResultRoute(result);

      if (!route) {
        console.error('Navigation route not resolved for result:', result);
        return;
      }

      navigate(route);

      // Close dropdown on successful navigation (typeFilters persist)
      setIsOpen(false);
      setSearchQuery('');
      setDisplayMode('active');

      // Call onClose callback if provided
      if (onClose) {
        onClose();
      }
    },
    [getResultRoute, navigate, onClose],
  );

  // Handle dropdown close (typeFilters persist)
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setInputValue('');
    setDisplayMode('active');
    setUseFixedPosition(false);

    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // Handle trigger click
  const handleTriggerClick = useCallback(() => {
    setUseFixedPosition(false); // Always use normal positioning when clicked directly
    setIsOpen(prev => !prev);
  }, []);

  // Helper to check if trigger is in view
  const checkTriggerInView = useCallback(() => {
    const referenceEl = refs.reference.current;
    if (referenceEl && 'getBoundingClientRect' in referenceEl) {
      const rect = referenceEl.getBoundingClientRect();
      return rect.top >= -50 && rect.bottom <= window.innerHeight + 50;
    }
    return true;
  }, [refs.reference]);

  // Global keyboard shortcut (Cmd+K on Mac, Ctrl+K on Windows/Linux)
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const isModifierPressed = isMac() ? e.metaKey : e.ctrlKey;

      if (isModifierPressed && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        // Use fixed positioning if trigger is not in view (page scrolled)
        const triggerInView = checkTriggerInView();
        setUseFixedPosition(!triggerInView);
        setIsOpen(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [checkTriggerInView]);

  // Auto-open search when user starts typing and no modal is present
  useEffect(() => {
    const handleTypeToOpen = (e: KeyboardEvent) => {
      if (isOpen) return;

      const target = e.target as HTMLElement | null;
      const tag = target?.tagName.toLowerCase();
      const isFormField =
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        tag === 'button' ||
        target?.isContentEditable;

      if (isFormField) return;
      if (document.querySelector('.mm-general-modal')) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      // Only trigger on printable characters (length 1), but NOT space
      // Space is commonly used for activation/selection, not typing
      if (e.key.length !== 1 || e.key === ' ') return;

      // Prevent the key from also entering the input after opening
      e.preventDefault();

      // Check if trigger is in view - if not, use fixed positioning
      const triggerInView = checkTriggerInView();

      setUseFixedPosition(!triggerInView);
      setIsOpen(true);
      // Convert Cyrillic to English layout for users who forget to switch keyboard
      const convertedKey = toEnglishLayout(e.key);
      setInputValue(convertedKey);
      const { cleanQuery, platforms } = extractPlatformFilters(convertedKey);
      setSearchQuery(cleanQuery);
      setPlatformFilters(platforms);
      // Defer focus until dropdown renders
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
    };

    document.addEventListener('keydown', handleTypeToOpen);
    return () => document.removeEventListener('keydown', handleTypeToOpen);
  }, [isOpen, checkTriggerInView]);

  return (
    <>
      {/* Trigger button */}
      <div ref={refs.setReference} {...getReferenceProps()}>
        {trigger({ onClick: handleTriggerClick, isOpen })}
      </div>

      {/* Dropdown portal */}
      <AnimatePresence>
        {isOpen && (
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              style={
                useFixedPosition
                  ? {
                      position: 'fixed',
                      top: '16px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 1000,
                    }
                  : {
                      ...floatingStyles,
                      zIndex: 1000,
                    }
              }
              {...getFloatingProps()}
              className="w-full sm:w-auto"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -8 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="w-[calc(100vw-32px)] sm:w-[590px] max-h-[80vh] sm:max-h-[560px] overflow-hidden rounded-2xl border border-white/10 bg-[rgba(24,24,28,0.95)] sm:bg-[rgba(24,24,28,0.75)] backdrop-blur-xl backdrop-saturate-150 text-[#eef1ff] shadow-[0_24px_80px_rgba(0,0,0,0.5),0_0_1px_rgba(255,255,255,0.1)_inset]"
              >
                <PairSearchDropdownErrorBoundary isAdmin={isAdmin} onClose={handleClose}>
                  <PairSearchDropdownPanel
                    inputValue={inputValue}
                    searchQuery={searchQuery}
                    displayMode={displayMode}
                    typeFilters={typeFilters}
                    platformFilters={platformFilters}
                    isOpen={isOpen}
                    searchInputRef={searchInputRef}
                    onInputValueChange={setInputValue}
                    onSearchQueryChange={setSearchQuery}
                    onDisplayModeChange={setDisplayMode}
                    onTypeFiltersChange={setTypeFilters}
                    onPlatformFiltersChange={setPlatformFilters}
                    onSelectResult={handleSelectResult}
                    onClose={handleClose}
                    onToggleFavorite={handleToggleFavorite}
                    onAddProjectToWatchlist={handleAddProjectToWatchlist}
                    onRemoveProjectFromWatchlist={handleRemoveProjectFromWatchlist}
                    favoriteProjectsSet={favoriteProjectsSet}
                  />
                </PairSearchDropdownErrorBoundary>
              </motion.div>
            </div>
          </FloatingPortal>
        )}
      </AnimatePresence>
    </>
  );
}
